import React, { useState } from 'react';
import { HardDrive } from 'lucide-react';
import NoticeBar from '../ui/NoticeBar';
import { TYPE } from '../../constants/typeScale';

/**
 * The custom, non-generative-UI notice cards a chat message can render from its `spec`:
 * a "storage full" upgrade nudge and an "add this specialist" agent tip. Extracted from
 * MessageBubble so the bubble focuses on message layout + actions. Purely presentational
 * (the agent tip keeps only its own in-flight busy/done state); actions come in as props.
 */

/** Gentle "your storage is full → get more room" card (links to plans; no hard sell). */
export const StorageUpgradeCard: React.FC = () => (
  <NoticeBar to="/wallet" icon={<HardDrive className="h-4 w-4" />} data-testid="storage-upgrade" className="max-w-sm">
    <span className={`${TYPE.control}`}>Storage's full — free some up, or <span className="font-medium underline">get a plan with much more room</span>.</span>
  </NoticeBar>
);

/**
 * Subtle, monochrome "add this specialist" tip — the group can't do what was asked, but someone
 * on the platform can. The agent NAME is bolded so it's unmistakably a name (some names read as
 * verbs, e.g. "Scout"/"Frame"). Renders from the spec when present, else the plain content text.
 */
export const AgentTipNote: React.FC<{
  spec: any; content: string;
  onAdd?: (agentId: string) => Promise<void> | void;
  onConsult?: (agentId: string) => Promise<void> | void;
}> = ({ spec, content, onAdd, onConsult }) => {
  const name = spec?.name as string | undefined;
  const agentId = spec?.agentId as string | undefined;
  const [busy, setBusy] = useState<null | 'add' | 'ask'>(null);
  const [done, setDone] = useState<null | 'added' | 'asked'>(null);
  const run = async (which: 'add' | 'ask') => {
    const fn = which === 'add' ? onAdd : onConsult;
    if (!fn || !agentId || busy || done) return;
    setBusy(which);
    try { await fn(agentId); setDone(which === 'add' ? 'added' : 'asked'); } catch { /* surfaced upstream */ } finally { setBusy(null); }
  };
  if (!name) {
    return <p className={`text-center ${TYPE.control} text-secondary-500 dark:text-secondary-400 max-w-md`} data-testid="agent-tip">{content}</p>;
  }
  const canAct = !!agentId && (!!onAdd || !!onConsult);
  return (
    <div className="flex flex-col items-center gap-2 max-w-md text-center" data-testid="agent-tip">
      <p className={`${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
        <strong className="font-semibold text-secondary-700 dark:text-secondary-200">{name}</strong>
        {spec.persona ? <> — a {spec.persona}</> : null}{spec.verb ? <> — could {spec.verb}.</> : '.'}
      </p>
      {canAct && (
        <div className="flex items-center gap-2">
          {onConsult && (
            <button onClick={() => run('ask')} disabled={!!busy || !!done}
              className={`rounded-full border border-secondary-300 dark:border-secondary-600 px-3 py-1 ${TYPE.control} font-medium text-secondary-700 dark:text-secondary-200 hover:border-secondary-500 disabled:opacity-60`}>
              {busy === 'ask' ? 'Asking…' : done === 'asked' ? `Asked ${name}` : `Ask ${name} once`}
            </button>
          )}
          {onAdd && (
            <button onClick={() => run('add')} disabled={!!busy || !!done}
              className={`rounded-full bg-secondary-900 dark:bg-white px-3 py-1 ${TYPE.control} font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-60`}>
              {busy === 'add' ? 'Adding…' : done === 'added' ? 'Added' : 'Add to group'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
